/**
 * Scenario React Query Hooks
 * 시나리오 관리 React Query 훅
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import {
	getScenarioListByUserId,
	getScenarioAdminList,
	getScenarioListAdmin,
	getScenarioDetailWithBasicFallback,
	createScenario,
	deleteScenario,
	getScenarioPlayRecords,
	type GetScenarioPlayRecordParams,
	type ScenarioDetailOrBasicResult,
} from '../services/scenarioService';
import type {
	GetScenarioListByUserIdResponseDto,
	CreateScenarioRequestDto,
	GetScenarioAdminListResponseDto,
	GetScenarioListAdminRawResponseDto,
	GetScenarioPlayRecordResponseDto,
} from '../types/api/scenario';

// Query Keys
export const scenarioKeys = {
	all: ['scenarios'] as const,
	lists: () => [...scenarioKeys.all, 'list'] as const,
	byUserId: (params: { page: number; pageSize: number; language?: string }) =>
		[...scenarioKeys.lists(), 'userId', params] as const,
	adminList: (params: Record<string, any>) => [...scenarioKeys.lists(), 'admin', params] as const,
	playRecords: (params?: GetScenarioPlayRecordParams) =>
		[...scenarioKeys.all, 'playRecord', params ?? {}] as const,
	details: () => [...scenarioKeys.all, 'detail'] as const,
	detail: (scenarioId: number) => [...scenarioKeys.details(), scenarioId] as const,
};

/**
 * 본인이 생성한 시나리오 목록 조회
 * ⚠️ Admin 페이지에는 부적합 (본인 시나리오만 조회)
 */
export const useScenarioListByUserId = (
	params: {
		page: number;
		pageSize: number;
	},
	options?: Omit<UseQueryOptions<GetScenarioListByUserIdResponseDto>, 'queryKey' | 'queryFn'>
) => {
	const language = useApiLanguage();
	return useQuery({
		queryKey: scenarioKeys.byUserId({ ...params, language }),
		queryFn: () => getScenarioListByUserId({ ...params, language }),
		enabled: params.page > 0 && params.pageSize > 0,
		staleTime: 0, // 항상 최신 데이터 — 등록·삭제 후 목록 즉시 반영
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		...options,
	});
};

/**
 * Admin용 시나리오 목록 조회
 * Real API 사용 (GET /scenario/admin/list)
 */
export const useScenarioAdminList = (
	params: {
		page: number;
		pageSize: number;
		status?: string;
		category?: string;
		platform?: string;
		search?: string;
		startDate?: string;
		endDate?: string;
	},
	options?: Omit<UseQueryOptions<GetScenarioAdminListResponseDto>, 'queryKey' | 'queryFn'>
) => {
	const language = useApiLanguage();
	return useQuery({
		queryKey: scenarioKeys.adminList({ ...params, language }),
		queryFn: () => getScenarioAdminList({ ...params, language }),
		enabled: options?.enabled !== false, // 기본적으로 활성화, options에서 false로 설정 가능
		staleTime: 3 * 60 * 1000, // 3분
		retry: false, // 에러 발생 시 재시도하지 않음
		...options,
	});
};

/**
 * 시나리오 상세 조회 — /detail 실패 시 /info 기본정보로 폴백 (플로우·START 스테이지 없는 건 등)
 */
export const useScenarioDetail = (
	scenarioId: number,
	options?: Omit<UseQueryOptions<ScenarioDetailOrBasicResult>, 'queryKey' | 'queryFn'>
) => {
	const language = useApiLanguage();
	return useQuery({
		queryKey: [...scenarioKeys.detail(scenarioId), language],
		queryFn: () => getScenarioDetailWithBasicFallback(scenarioId, language),
		enabled: !!scenarioId && (options?.enabled !== false),
		staleTime: 5 * 60 * 1000, // 5분
		...options,
	});
};

/**
 * 시나리오 생성
 * ✅ 사용 가능
 */
export const useCreateScenario = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateScenarioRequestDto) => createScenario(data),
		onSuccess: () => {
			// 시나리오 목록 캐시 무효화
			queryClient.invalidateQueries({ queryKey: scenarioKeys.lists() });
		},
	});
};

export const useDeleteScenario = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (scenarioId: number) => deleteScenario(scenarioId),
		onSuccess: (_data, scenarioId) => {
			queryClient.invalidateQueries({ queryKey: scenarioKeys.lists() });
			queryClient.removeQueries({ queryKey: scenarioKeys.detail(scenarioId) });
		},
	});
};

/**
 * 시나리오 플레이 기록 조회
 * Phase 11: GET /scenario-play-record
 */
export const useScenarioPlayRecords = (
	params?: GetScenarioPlayRecordParams,
	options?: Omit<
		UseQueryOptions<GetScenarioPlayRecordResponseDto>,
		'queryKey' | 'queryFn'
	>
) => {
	return useQuery({
		queryKey: scenarioKeys.playRecords(params),
		queryFn: () => getScenarioPlayRecords(params),
		staleTime: 2 * 60 * 1000, // 2분
		...options,
	});
};

/**
 * 프로덕트·클래스 커리큘럼 시나리오 추가 모달용 목록
 * — `/scenario/list/admin` API 사용 (creatorName, expectedPlayTimeSeconds 필드 포함)
 * — `category`·`platform`·`search` 는 백엔드가 지원하는 경우 `getScenarioListAdmin` 로 전달
 */
export const useScenarioListByOption = (
	params: {
		page: number;
		pageSize: number;
		category?: string;
		platform?: string;
		search?: string;
	},
	options?: Omit<UseQueryOptions<GetScenarioListAdminRawResponseDto>, 'queryKey' | 'queryFn'>
) => {
	const language = useApiLanguage();
	return useQuery({
		queryKey: [...scenarioKeys.lists(), 'listOption', params, language],
		queryFn: () => getScenarioListAdmin({ ...params, language }),
		enabled: options?.enabled !== false,
		staleTime: 2 * 60 * 1000,
		...options,
	});
};
